import React, { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { toast } from "../hooks/use-toast";
import { Sparkles, CheckCircle2 } from "lucide-react";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ApplicationDialog = ({ open, onOpenChange }) => {
  const { t, lang } = useLang();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", country: "", program: "", message: "" });
  const handleChange = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast({ title: t("form.required"), description: t("form.requiredDesc"), variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { ...form, lang, source: window.location.pathname });
      setSubmitted(true);
      toast({ title: t("form.sent"), description: t("form.sentDesc") });
    } catch (err) {
      toast({ title: "Ошибка", description: "Попробуйте ещё раз", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(() => { setSubmitted(false); setForm({ name: "", phone: "", email: "", country: "", program: "", message: "" }); }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto rounded-[1.5rem]">
        {!submitted ? (
          <>
            <DialogHeader>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold w-fit mb-2 uppercase tracking-widest"><Sparkles className="w-3 h-3" /> {t("form.badge")}</div>
              <DialogTitle className="font-display text-2xl uppercase">{t("form.title")}</DialogTitle>
              <DialogDescription>{t("form.desc")}</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="name" className="text-xs">{t("form.name")} *</Label>
                  <Input id="name" value={form.name} onChange={e => handleChange("name", e.target.value)} placeholder={t("form.namePh")} className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-xs">{t("form.phone")} *</Label>
                  <Input id="phone" value={form.phone} onChange={e => handleChange("phone", e.target.value)} placeholder="+380 ..." className="mt-1" />
                </div>
              </div>
              <div>
                <Label htmlFor="email" className="text-xs">{t("form.email")}</Label>
                <Input id="email" type="email" value={form.email} onChange={e => handleChange("email", e.target.value)} placeholder="you@email.com" className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t("form.country")}</Label>
                  <Select value={form.country} onValueChange={(v) => handleChange("country", v)}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder={t("form.choose")} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="uk">{t("countries.items.uk.name")}</SelectItem>
                      <SelectItem value="usa">{t("countries.items.usa.name")}</SelectItem>
                      <SelectItem value="nl">{t("countries.items.nl.name")}</SelectItem>
                      <SelectItem value="ch">{t("countries.items.ch.name")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{t("form.program")}</Label>
                  <Select value={form.program} onValueChange={(v) => handleChange("program", v)}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder={t("form.choose")} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="foundation">{t("form.programs.foundation")}</SelectItem>
                      <SelectItem value="bachelor">{t("form.programs.bachelor")}</SelectItem>
                      <SelectItem value="master">{t("form.programs.master")}</SelectItem>
                      <SelectItem value="school">{t("form.programs.school")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="msg" className="text-xs">{t("form.message")}</Label>
                <Textarea id="msg" value={form.message} onChange={e => handleChange("message", e.target.value)} placeholder={t("form.msgPh")} className="mt-1 min-h-[80px]" />
              </div>
              <Button type="submit" disabled={submitting} className="w-full btn-amber rounded-full h-12 font-semibold mt-2">{submitting ? '...' : t("cta.sendRequest")}</Button>
              <p className="text-[11px] text-slate-500 text-center">{t("form.privacy")}</p>
            </form>
          </>
        ) : (
          <div className="py-8 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-4"><CheckCircle2 className="w-8 h-8" /></div>
            <DialogTitle className="font-display text-2xl uppercase">{t("form.accepted")}</DialogTitle>
            <DialogDescription className="mt-2">{t("form.acceptedDesc")}</DialogDescription>
            <Button onClick={() => handleClose(false)} className="mt-6 btn-amber rounded-full h-11 px-6 font-semibold">{t("form.great")}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ApplicationDialog;
